import React from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { routeTree } from './routeTree.gen';
import { initSentry, Sentry } from './lib/sentry';

// Sentry.ErrorBoundary is a class component whose props include a
// `context` field that TypeScript treats as required by
// `Component<any, any, any>` under React 18 types on some pnpm
// resolutions — the built-in JSX check then rejects it as "not a valid
// JSX element type". Casting through ComponentType silences the check
// without changing runtime behavior.
const SentryErrorBoundary = Sentry.ErrorBoundary as unknown as React.ComponentType<{
  fallback: React.ReactNode;
  children?: React.ReactNode;
}>;
import { PasswordGate } from './components/PasswordGate';
import './index.css';

const PREVIEW_PASSWORD = import.meta.env.VITE_PREVIEW_PASSWORD as string | undefined;

initSentry();

const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Root element #root not found');

function FallbackError(): React.ReactElement {
  return (
    <main style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', fontFamily: 'system-ui, sans-serif' }}>
      <div style={{ textAlign: 'center' }}>
        <p style={{ fontSize: 12, letterSpacing: 1.5, textTransform: 'uppercase', color: '#525252', marginBottom: 8 }}>
          LYN Rx
        </p>
        <h1 style={{ fontSize: 32, fontWeight: 600, color: '#0A0A0A', margin: 0 }}>Something went wrong</h1>
        <p style={{ fontSize: 14, color: '#525252', marginTop: 8 }}>The error has been reported. Please refresh.</p>
      </div>
    </main>
  );
}

const gatedApp = PREVIEW_PASSWORD ? (
  <PasswordGate expected={PREVIEW_PASSWORD}>
    <RouterProvider router={router} />
  </PasswordGate>
) : (
  <RouterProvider router={router} />
);

ReactDOM.createRoot(rootEl).render(
  <React.StrictMode>
    <SentryErrorBoundary fallback={<FallbackError />}>
      {gatedApp}
    </SentryErrorBoundary>
  </React.StrictMode>
);
